import { Link } from 'react-router-dom';
import { isFinal, isLive, kickoff, type Game, type TeamSide } from '../lib/api';
import { logo } from '../lib/logo';
import { teamColor } from '../lib/colors';
import { Score } from './Score';

function Row({ t, started, winner, loser }: { t: TeamSide; started: boolean; winner: boolean; loser: boolean }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      {t.logo ? <img src={logo(t.logo, 28)} alt="" className="h-7 w-7 shrink-0 object-contain" loading="lazy" />
        : <span className="h-7 w-7 shrink-0 rounded-sm" style={{ background: teamColor(t.color) }} />}
      <div className="min-w-0 flex-1">
        <p className={`truncate font-display text-[15px] leading-tight ${loser ? 'font-semibold text-muted' : 'font-bold'}`}>
          {t.rank ? <span className="num mr-1 text-[11px] align-top text-blue">{t.rank}</span> : null}
          {t.name}
        </p>
        <p className="meta !text-[11px] leading-tight">{t.abbreviation}{t.record ? `  ${t.record}` : ''}</p>
      </div>
      {started && <Score value={String(t.score ?? 0)} className={`score text-[26px] leading-none ${loser ? 'text-muted' : 'text-ink'}`} />}
      {started && winner && <span className="h-0 w-0 border-y-[5px] border-r-[7px] border-y-transparent border-r-ink" aria-label="winner" />}
    </div>
  );
}

/** A scoreboard block: team color on the leading edge, figures right-aligned. */
export function GameCard({ g, tab, action, run = false }: { g: Game; tab?: string; action?: string; run?: boolean }) {
  const live = isLive(g), final = isFinal(g), started = live || final;
  const k = kickoff(g.date);
  const a = Number(g.away_team.score ?? 0), h = Number(g.home_team.score ?? 0);
  const accent = live ? '#DC2626' : teamColor(g.home_team.color);
  return (
    <Link to={`/game/${g.id}${tab ? `?tab=${tab}${run ? '&run=1' : ''}` : ''}`}
      className="card-hover group relative block overflow-hidden" style={{ color: accent }}>
      {/* the home team's mark, oversized and faint, the way a broadcast lower third does it */}
      {g.home_team.logo && (
        <img src={logo(g.home_team.logo, 112)} alt="" aria-hidden="true" loading="lazy"
          className="pointer-events-none absolute -right-6 -top-4 h-28 w-28 object-contain opacity-[0.07] transition-opacity duration-200 group-hover:opacity-[0.13]" />
      )}
      <div className="edge-row relative pr-3">
        <div className="flex items-center justify-between gap-3 py-2">
          <span className="meta">{g.league === 'cfb' ? 'CFB' : 'NFL'}</span>
          {live
            ? <span className="pill bg-red text-paper">{g.status_detail}</span>
            : <span className="meta">{final ? 'Final' : `${k.day} ${k.time}`}</span>}
        </div>
        <div className="border-t border-line py-1 text-ink">
          <Row t={g.away_team} started={started} winner={final && a > h} loser={final && a < h} />
          <Row t={g.home_team} started={started} winner={final && h > a} loser={final && h < a} />
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-line py-2">
          <span className="num truncate text-[12px] text-muted">{g.odds ?? g.venue ?? ''}</span>
          <span className="font-cond text-[14px] font-semibold uppercase tracking-[0.08em] text-blue transition-transform duration-150 group-hover:translate-x-0.5">
            {action ?? (final ? 'Grades' : 'Analyze')} &rsaquo;
          </span>
        </div>
      </div>
    </Link>
  );
}
